import { CheckCircle, AlertTriangle, Info } from 'lucide-react'
import type { ValidationStatus as ValidationStatusType } from '../../types'

interface ValidationStatusProps {
  status: ValidationStatusType
}

const ICON_MAP = {
  error: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
}

const COLOR_MAP = {
  error: 'var(--fn-status-error)',
  warning: 'var(--fn-status-warning)',
  info: 'var(--fn-primary)',
}

export function ValidationStatus({ status }: ValidationStatusProps) {
  const progressPercent = Math.min(100, Math.max(0, status.completionPercent))

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        borderColor: 'var(--fn-border)',
        backgroundColor: 'var(--fn-bg-card)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-fn-text">
          Validation Status
        </h3>
        {status.isValid && (
          <CheckCircle className="w-4 h-4 text-fn-active" />
        )}
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-fn-text-muted">Completion</span>
          <span className="font-medium text-fn-text">
            {progressPercent}%
          </span>
        </div>
        <div
          className="w-full h-2 rounded-full overflow-hidden bg-fn-muted"
        >
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${progressPercent}%`,
              backgroundColor: status.isValid ? 'var(--fn-status-success)' : 'var(--fn-primary)',
            }}
          />
        </div>
      </div>

      {status.issues.length > 0 && (
        <div className="space-y-2">
          {status.issues.map((issue, index) => {
            const Icon = ICON_MAP[issue.type]
            const color = COLOR_MAP[issue.type]

            return (
              <div
                key={index}
                className="flex items-start gap-2 p-2 rounded bg-fn-hover"
              >
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-fn-text">
                    {issue.message}
                  </p>
                  {issue.field && (
                    <p className="text-xs mt-0.5 text-fn-text-muted">
                      Field: {issue.field}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {status.isValid && status.issues.length === 0 && (
        <div className="text-center py-3">
          <p className="text-xs text-fn-active">
            All checks passed
          </p>
        </div>
      )}
    </div>
  )
}
